import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  Check,
  ChevronDown,
  ClipboardList,
  Gauge,
  HandCoins,
  ScanSearch,
  Wrench,
} from "lucide-react";
import { PageShell, Price, usePageTitle, useSectionNav } from "./components/site/Layout";
import { buttons, container, field } from "./components/site/ui";
import { buildSummaryTiles, FaultList, Plate, ResultPill, StatTile } from "./components/report/ReportParts";
import { CONTACT_EMAIL, FREE_PROMO, REPORT_PRICE } from "./config";
import {
  analyseMileage,
  formatDate,
  groupDefects,
  isValidMileage,
  isValidReg,
  normaliseReg,
  parseMileage,
  riskBand,
  sortFaults,
  summariseMot,
  vehicleSummaryLine,
} from "./lib/report";
import { SAMPLE_MILEAGE, SAMPLE_REGISTRATION, SAMPLE_REPORT } from "./lib/sampleReport";

const FEATURES = [
  {
    icon: ClipboardList,
    title: "Full MOT history",
    text: "Every MOT on record: pass or fail, the date, the recorded mileage, failure items and advisories.",
  },
  {
    icon: Gauge,
    title: "Mileage check",
    text: "The odometer reading from each MOT on a timeline. If the mileage ever goes down, the report flags it.",
  },
  {
    icon: Wrench,
    title: "Known faults for that model",
    text: "The faults the car is most likely to have for its age, engine and MOT record, ranked by risk.",
  },
  {
    icon: ScanSearch,
    title: "What to look for",
    text: "Where each fault shows up, what to look and listen for at the viewing, and the usual fix.",
  },
  {
    icon: Activity,
    title: "Risk score",
    text: "One score from 0 to 100, where lower is better, so you can compare cars side by side.",
  },
  {
    icon: HandCoins,
    title: "Repair costs to negotiate with",
    text: "Estimated UK garage prices for each fault, so you know what to ask off the price.",
  },
];

const STEPS = [
  {
    title: "Check before you travel",
    text: "Run the registration from the advert. If the MOT record doesn't match what the seller told you, you've saved a wasted trip.",
  },
  {
    title: "Compare the mileage",
    text: "Enter the mileage from the advert. It should be higher than the last MOT reading. If it's lower, ask why.",
  },
  {
    title: "Inspect the flagged faults",
    text: "Take the report to the viewing. Each fault says where to look, what to listen for and what the fix usually costs.",
  },
  {
    title: "Negotiate with evidence",
    text: "Repeated advisories and likely repair costs are solid reasons to ask for money off, or to walk away.",
  },
];

const INCLUDED = [
  "Full MOT history from the DVSA",
  "Mileage check against every MOT",
  "Known faults with repair cost estimates",
  "Risk score and buying advice",
  "Make and model search when you don't have the registration",
];

const FAQS = [
  {
    question: "Where does the information come from?",
    answer:
      "MOT results, mileage readings and vehicle details come from the DVSA's MOT history service. The fault analysis is generated by AI from that record and the known issues for the model, so treat repair costs as estimates.",
  },
  {
    question: FREE_PROMO ? "Is it really free?" : "How much does a report cost?",
    answer: FREE_PROMO
      ? "For a limited time, yes. Every report is free and you won't be asked for card details."
      : `Each report costs ${REPORT_PRICE}. There's no subscription.`,
  },
  {
    question: "Do I need an account?",
    answer: "No. You only need one if you want to save reports and find them again later.",
  },
  {
    question: "Can I check a car without the registration?",
    answer:
      "Yes. Search by make, model, year and mileage to see the faults that car is known for. MOT history is tied to the registration, so it isn't included.",
  },
  {
    question: "Does this replace a mechanic's inspection?",
    answer:
      "No. It tells you what to look for and what to ask the seller, but it can't see the car. If you're unsure, have it inspected before you buy.",
  },
];

function SearchForm() {
  const navigate = useNavigate();
  const [reg, setReg] = useState("");
  const [mileage, setMileage] = useState("");
  const [errors, setErrors] = useState({});

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!isValidReg(reg)) nextErrors.reg = "Enter a UK registration, for example AB12 CDE.";
    if (!isValidMileage(mileage)) nextErrors.mileage = "Enter the mileage from the advert (1 to 400,000).";
    setErrors(nextErrors);
    if (nextErrors.reg || nextErrors.mileage) {
      document.getElementById(nextErrors.reg ? "reg-input" : "mileage-input")?.focus();
      return;
    }

    const params = new URLSearchParams({ registration: normaliseReg(reg), mileage: String(parseMileage(mileage)) });
    navigate(`/results?${params}`);
  };

  const clearError = (name) => setErrors((current) => (current[name] ? { ...current, [name]: undefined } : current));

  return (
    <form
      id="search"
      onSubmit={handleSubmit}
      noValidate
      className="mt-8 scroll-mt-24 rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
    >
      <div className="grid gap-5 sm:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <div>
          <label htmlFor="reg-input" className={field.label}>
            Registration
          </label>
          <div
            className={`mt-1.5 flex h-14 items-stretch overflow-hidden rounded-md border-2 bg-[#FFD307] focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 ${
              errors.reg ? "border-red-600" : "border-slate-900"
            }`}
          >
            <span className="flex w-9 shrink-0 flex-col items-center justify-end bg-blue-700 pb-1.5 text-[10px] font-bold text-white" aria-hidden="true">
              UK
            </span>
            <input
              id="reg-input"
              value={reg}
              onChange={(event) => {
                setReg(event.target.value.toUpperCase());
                clearError("reg");
              }}
              placeholder="AB12 CDE"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={10}
              aria-invalid={Boolean(errors.reg)}
              aria-describedby={errors.reg ? "reg-error" : undefined}
              className="w-full min-w-0 bg-transparent px-3 text-center text-2xl font-bold uppercase tracking-[0.1em] text-slate-900 placeholder:text-slate-900/30 focus:outline-none"
            />
          </div>
          {errors.reg && (
            <p id="reg-error" className={field.error}>
              {errors.reg}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="mileage-input" className={field.label}>
            Mileage
          </label>
          <input
            id="mileage-input"
            inputMode="numeric"
            value={mileage}
            onChange={(event) => {
              setMileage(event.target.value.replace(/[^0-9]/g, "").slice(0, 6));
              clearError("mileage");
            }}
            placeholder="e.g. 45000"
            aria-invalid={Boolean(errors.mileage)}
            aria-describedby={errors.mileage ? "mileage-error" : "mileage-hint"}
            className={`${field.input} h-14 text-lg ${errors.mileage ? field.invalid : ""}`}
          />
          {errors.mileage ? (
            <p id="mileage-error" className={field.error}>
              {errors.mileage}
            </p>
          ) : (
            <p id="mileage-hint" className={field.hint}>
              As shown in the advert
            </p>
          )}
        </div>
      </div>

      <button type="submit" className={`${buttons.primary} mt-5 w-full py-3 text-base`}>
        Check this car
        <span aria-hidden="true" className="text-blue-300">
          ·
        </span>
        <Price />
      </button>

      <p className="mt-4 text-center text-sm text-slate-600">
        No registration?{" "}
        <Link to="/manual-search" className={buttons.link}>
          Search by make and model
        </Link>
      </p>
    </form>
  );
}

// A cut-down report built from sample data with the same components and
// calculations as the real results page.
function ReportPreview() {
  const { vehicleInfo, motHistory, faults, riskScore } = SAMPLE_REPORT;
  const mot = summariseMot(motHistory);
  const mileage = analyseMileage(motHistory, SAMPLE_MILEAGE);
  const tiles = buildSummaryTiles({ mot, mileage, vehicleInfo });
  const band = riskBand(riskScore);
  const latestAdvisories = groupDefects(mot.latest.defects).advisories;
  const bandText = { good: "text-emerald-700", warning: "text-amber-700", critical: "text-red-700" }[band.tone];

  return (
    <div className="mx-auto w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 lg:max-w-none">
      <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Report preview · sample data</p>
        <Link to="/sample-report" className={`${buttons.link} text-sm`}>
          Full report
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-4 px-5 pt-5">
        <Plate registration={SAMPLE_REGISTRATION} />
        <div>
          <p className="font-bold text-slate-900">
            {vehicleInfo.make} {vehicleInfo.model}
          </p>
          <p className="text-sm text-slate-500">{vehicleSummaryLine(vehicleInfo)}</p>
        </div>
      </div>

      <div className="mx-5 mt-5 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-3">
        <div className="bg-white p-3">
          <p className="text-xs text-slate-500">Risk score</p>
          <p className="mt-1">
            <span className="text-2xl font-bold tracking-tight text-slate-900">{riskScore}</span>
            <span className="text-xs font-medium text-slate-500">/100</span>
          </p>
          <p className={`text-xs font-semibold ${bandText}`}>{band.label}</p>
        </div>
        <StatTile size="sm" {...tiles.lastTest} />
        <StatTile size="sm" {...tiles.mileage} />
      </div>

      <div className="px-5 pt-5">
        <h3 className="text-sm font-semibold text-slate-900">Top faults to check</h3>
        <FaultList faults={sortFaults(faults).slice(0, 3)} compact />
      </div>

      <div className="border-t border-slate-200 bg-slate-50 px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-slate-900">Latest MOT</p>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <ResultPill test={mot.latest} />
            {formatDate(mot.latest.completedDate)}
          </div>
        </div>
        <ul className="mt-2 space-y-1 text-sm text-slate-600">
          {latestAdvisories.slice(0, 2).map((defect) => (
            <li key={defect.text} className="flex gap-2">
              <span className="mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" aria-hidden="true" />
              <span className="line-clamp-1">{defect.text}</span>
            </li>
          ))}
        </ul>
        {latestAdvisories.length > 2 && (
          <p className="mt-1 pl-3.5 text-xs text-slate-500">
            and {latestAdvisories.length - 2} more {latestAdvisories.length - 2 === 1 ? "advisory" : "advisories"}
          </p>
        )}
      </div>
    </div>
  );
}

function SectionHeading({ title, children }) {
  return (
    <div className="max-w-xl">
      <h2 className="text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
      <div className="mt-4 text-base leading-7 text-slate-600">{children}</div>
    </div>
  );
}

export default function CarSenseUI() {
  usePageTitle();
  const goToSection = useSectionNav();
  const { hash, key } = useLocation();

  // Arriving from another page with /#pricing etc. jumps to that section.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: "start" });
  }, [hash, key]);

  return (
    <PageShell>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className={`${container} grid items-start gap-12 py-12 sm:py-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,34rem)] lg:gap-16 lg:py-20`}>
          <div className="max-w-xl">
            <p className="text-sm font-semibold text-blue-700">UK used car checks</p>
            <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Check a used car before you buy it
            </h1>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              Enter the registration to see the car's full MOT history, a mileage check and the faults most likely to
              cost you money on that model, with what to look for at the viewing.
            </p>
            <SearchForm />
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
              {["Official DVSA data", "No account needed", "No card details"].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <ReportPreview />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="scroll-mt-16 border-b border-slate-200">
        <div className={`${container} grid gap-12 py-16 sm:py-24 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-16`}>
          <div>
            <SectionHeading title="What's in a report">
              <p>
                Everything comes from one registration. MOT history and mileage come straight from the DVSA, and the
                fault analysis is built from that record and the known issues for the model.
              </p>
            </SectionHeading>
            <Link to="/sample-report" className={`${buttons.secondary} mt-8`}>
              See a sample report
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <dl className="grid gap-x-10 gap-y-10 sm:grid-cols-2">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="border-t border-slate-200 pt-6">
                <dt className="flex items-center gap-3 font-semibold text-slate-900">
                  <Icon className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
                  {title}
                </dt>
                <dd className="mt-2 text-sm leading-6 text-slate-600">{text}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Guide */}
      <section id="guide" className="scroll-mt-16 border-b border-slate-200 bg-slate-50">
        <div className={`${container} py-16 sm:py-24`}>
          <SectionHeading title="How to use Car Sense when you buy">
            <p>Four steps, from reading the advert to agreeing a price.</p>
          </SectionHeading>

          <ol className="mt-12 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step, index) => (
              <li key={step.title} className="border-t-2 border-blue-600 pt-5">
                <p className="text-sm font-semibold text-blue-700">Step {index + 1}</p>
                <h3 className="mt-2 font-semibold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{step.text}</p>
              </li>
            ))}
          </ol>

          <div className="mt-14 flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">Don't have the registration?</h3>
              <p className="mt-1 text-sm text-slate-600">
                Search by make, model and year to see the faults that car is known for. MOT history needs the
                registration.
              </p>
            </div>
            <Link to="/manual-search" className={`${buttons.secondary} shrink-0`}>
              Search by make and model
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="scroll-mt-16 border-b border-slate-200">
        <div className={`${container} grid items-center gap-12 py-16 sm:py-24 lg:grid-cols-2 lg:gap-16`}>
          <SectionHeading title="Simple pricing">
            <p>Pay for a report only when you need one. There's no subscription and you don't need an account.</p>
            {FREE_PROMO && (
              <p className="mt-4">
                For a limited time every report is free, so you can check as many cars as you like while you shop
                around.
              </p>
            )}
          </SectionHeading>

          <div className="rounded-xl border-2 border-blue-600 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-slate-900">Vehicle report</h3>
              {FREE_PROMO && (
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-600/20">
                  Limited time offer
                </span>
              )}
            </div>
            <p className="mt-5">
              <Price
                className="text-4xl font-bold tracking-tight text-slate-900"
                strikeClassName="text-2xl font-medium text-slate-400"
              />
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {FREE_PROMO ? "On every report while the offer lasts" : "per report"}
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {INCLUDED.map((item) => (
                <li key={item} className="flex gap-3">
                  <Check className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => goToSection("search")} className={`${buttons.primary} mt-8 w-full py-3`}>
              {FREE_PROMO ? "Check a car for free" : "Check a car"}
            </button>
            {FREE_PROMO && <p className="mt-3 text-center text-xs text-slate-500">No card details needed.</p>}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className={`${container} grid gap-12 py-16 sm:py-24 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-16`}>
          <SectionHeading title="Questions">
            <p>
              Can't find what you need?{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className={buttons.link}>
                Email us
              </a>
              .
            </p>
          </SectionHeading>
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((faq) => (
              <details key={faq.question} className="group py-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 font-semibold text-slate-900 [&::-webkit-details-marker]:hidden">
                  {faq.question}
                  <ChevronDown className="h-5 w-5 shrink-0 text-slate-500 transition-transform group-open:rotate-180" aria-hidden="true" />
                </summary>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Closing call to action */}
      <section className="bg-blue-700">
        <div className={`${container} flex flex-col items-start justify-between gap-6 py-12 sm:flex-row sm:items-center`}>
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-white">Viewing a car this week?</h2>
            <p className="mt-2 text-blue-100">Run the registration before you go and take the report with you.</p>
          </div>
          <button
            type="button"
            onClick={() => goToSection("search")}
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-blue-700"
          >
            Check a car
            <span aria-hidden="true" className="text-blue-300">
              ·
            </span>
            <Price />
          </button>
        </div>
      </section>
    </PageShell>
  );
}
